import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../common/PageShell.jsx';
import Hero from '../Home/Hero.jsx';

const Home = () => {
  useEffect(() => {
    document.title = 'Eco World Capital | Financial Growth & Technology';
  }, []);

  const coreServices = [
    { title: 'Demat & Trading Account Assistance', desc: 'We assist clients in opening Demat and trading accounts through reputed financial institutions and brokerage partners.' },
    { title: 'Portfolio Management Support', desc: 'We provide portfolio monitoring, market research support, and investment strategy assistance designed to help clients optimize decisions.' },
    { title: 'Algorithmic Trading Solutions', desc: 'Advanced algorithmic trading systems utilizing predefined rules, automation, and data-driven execution methodologies.' },
    { title: 'Trading Software Solutions', desc: 'Facilitating lease, rental, and outright software purchases customized for Equity, F&O, Commodities, and Currencies.' },
  ];

  const loanServices = [
    'Business Loans & Working Capital',
    'Personal & Home Loans',
    'Mortgage Loans & Loan Against Property',
    'MSME Financing & Private Equity',
  ];

  const benefits = [
    { title: 'Earn First, Pay Later Model', desc: 'Selected services may be offered under performance-linked commercial arrangements, subject to eligibility and mutually agreed terms.' },
    { title: 'Demonstration Before Onboarding', desc: 'Clients may evaluate selected technology solutions and algorithmic systems through demonstrations before making a commitment.' },
    { title: 'Transparency', desc: 'We maintain a structured and transparent approach regarding service processes, technology deployment, and client engagement.' },
    { title: 'Technology-Driven Solutions', desc: 'Our solutions leverage modern algorithmic and automation technologies designed to improve efficiency and consistency.' },
  ];

  const partners = ['Bonanza Portfolio', 'Alice Blue', 'Exness Tech', 'Octa FX'];

  return (
    <PageShell>
      <Hero />

      <section id="about" className="py-24 px-6 md:px-12 relative bg-black/10 border-t border-white/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="section-badge">About Us</span>
            <h2 className="mt-8 text-4xl font-extrabold tracking-tight text-brand-cream">
              Combining Market Expertise & Innovative Technology
            </h2>
            <p className="mt-6 text-brand-cream-muted text-base leading-relaxed font-medium">
              Eco World Capital combines market expertise, innovative technology, and a client-centric approach to help clients achieve their financial goals efficiently and transparently. With extensive experience across Indian and global financial markets, our group of experts delivers high-performance results.
            </p>
          </div>

          <div className="glass-card p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-brand-cream/20 to-transparent pointer-events-none" aria-hidden="true" />
            <h3 className="text-xl font-extrabold text-brand-cream mb-4 tracking-wide relative z-10">Our Vision & Mission</h3>
            <p className="text-base text-brand-cream-muted font-semibold italic mb-6 relative z-10">
              "To make advanced financial technology accessible to investors and simplify financing processes."
            </p>
            <div className="space-y-3 text-sm text-brand-cream-muted font-medium relative z-10">
              {['Transparent and client-focused solutions', 'Disciplined and systematic market participation', 'Long-term value creation through innovation'].map((item) => (
                <div key={item} className="flex items-start space-x-3">
                  <span className="text-brand-gold-light font-black text-lg drop-shadow-sm">✓</span>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-24 px-6 md:px-12 relative border-t border-white/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <span className="section-badge">Our Offerings</span>
            <h2 className="text-4xl font-extrabold mt-8 text-brand-cream">Comprehensive Investment & Funding Solutions</h2>
          </div>

          <h3 className="text-lg font-bold tracking-wider text-brand-cream uppercase mb-8 border-l-4 border-brand-gold-light pl-4">
            Investment & Trading Solutions
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {coreServices.map((service, idx) => (
              <div key={idx} className="glass-card p-6 rounded-2xl hover:bg-brand-cream/20 hover:border-brand-gold-light/60 hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between shadow-lg shimmer-hover">
                <div className="relative z-10">
                  <h4 className="text-lg font-bold text-brand-cream mb-3 tracking-wide">{service.title}</h4>
                  <p className="text-sm text-brand-cream-muted/90 leading-relaxed font-medium">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-bold tracking-wider text-brand-cream uppercase mb-8 border-l-4 border-brand-gold-light pl-4">
            Loan Arrangement Services
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loanServices.map((loan, idx) => (
              <div key={idx} className="glass-card p-5 rounded-2xl hover:bg-brand-cream/20 hover:border-brand-gold-light/40 transition-all flex items-center space-x-4 shadow-md group">
                <span className="text-brand-primary text-xl bg-gradient-to-br from-brand-gold to-brand-gold-light w-10 h-10 rounded-xl flex items-center justify-center shadow-inner font-black group-hover:scale-110 transition-transform">
                  ✦
                </span>
                <span className="text-sm font-bold text-brand-cream tracking-wide">{loan}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="benefits" className="py-24 px-6 md:px-12 relative bg-black/10 border-t border-white/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <span className="section-badge">Why Choose Us</span>
            <h2 className="text-4xl font-extrabold mt-8 text-brand-cream">The EWC Client Advantage</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="glass-card p-8 hover:bg-brand-cream/20 hover:border-brand-gold-light/50 transition-all duration-300">
                <h3 className="text-xl font-extrabold text-brand-cream tracking-wide">{benefit.title}</h3>
                <p className="text-sm text-brand-cream-muted/90 mt-3 leading-relaxed font-medium">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="partners" className="py-20 px-6 md:px-12 relative border-t border-white/20">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xs font-bold tracking-widest text-brand-gold-light uppercase mb-10 drop-shadow-sm">
            Strategic Partnerships & Global Partners
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 justify-center items-center">
            {partners.map((name) => (
              <Link
                key={name}
                to="/franchise"
                className="text-base font-extrabold tracking-wider text-brand-bronze border border-brand-cream/50 bg-brand-cream py-4 px-4 rounded-2xl shadow-sm backdrop-blur-md hover:scale-105 hover:border-brand-gold-light transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
              >
                {name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Home;
